import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import MainLayout from "@/components/feature/MainLayout";
import { commercialUsers } from "@/mocks/users";
import CommercialPortrait from "./components/CommercialPortrait";

const aiInsights = [
  { icon: "ri-fire-line", color: "text-[#E8531A]", bg: "bg-orange-50", title: "高用气量预警", desc: "北京燃气热力工程设计院本月用气量较上月增长12.3%，建议核实是否有新增用气设备" },
  { icon: "ri-shield-check-line", color: "text-green-600", bg: "bg-green-50", title: "安检到期提醒", desc: "老北京涮肉馆安检到期日为2026-04-15，距今仅5天，请尽快安排安检" },
  { icon: "ri-file-text-line", color: "text-teal-600", bg: "bg-teal-50", title: "合同续签提醒", desc: "北京华联超市供气合同将于2026-06-30到期，建议提前1个月启动续签流程" },
  { icon: "ri-money-cny-circle-line", color: "text-amber-600", bg: "bg-amber-50", title: "欠费风险分析", desc: "AI预测：当前工商业用户整体缴费率97.8%，无高风险欠费用户" },
];

const contractData = [
  { id: "CON-2024-001", company: "老北京涮肉馆", type: "小餐饮供气合同", startDate: "2024-01-01", endDate: "2026-12-31", amount: "¥120,000/年", status: "有效" },
  { id: "CON-2024-002", company: "北京华联超市", type: "大型商业供气合同", startDate: "2024-07-01", endDate: "2026-06-30", amount: "¥580,000/年", status: "即将到期" },
  { id: "CON-2024-003", company: "北京燃气热力工程设计院", type: "特殊行业供气合同", startDate: "2023-03-15", endDate: "2028-03-14", amount: "¥2,400,000/年", status: "有效" },
];

export default function CommercialPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState("全部");
  const [activeTab, setActiveTab] = useState<"list" | "contract" | "analysis">("list");
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const types = ["全部", "小餐饮业", "特殊行业", "大型企事业"];
  const filtered = commercialUsers.filter(u => {
    const matchSearch = u.name.includes(search) || u.phone.includes(search) || u.id.includes(search);
    const matchType = filterType === "全部" || u.type === filterType;
    return matchSearch && matchType;
  });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || activeTab !== "analysis") return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const w = canvas.width;
    const h = canvas.height;
    ctx.clearRect(0, 0, w, h);

    const months = ["7月", "8月", "9月", "10月", "11月", "12月", "1月", "2月", "3月", "4月"];
    const usageData = [
      [880, 910, 1020, 1180, 1380, 1520, 1580, 1320, 1180, 1256],
      [6562, 6856, 7856, 8856, 9562, 10256, 10256, 9856, 8562, 8562],
      [17562, 18256, 22856, 26856, 30562, 33256, 32560, 28560, 25680, 25680],
    ];
    const colors = ["#E8531A", "#22c55e", "#06b6d4"];
    const labels = ["老北京涮肉馆", "北京华联超市", "燃气热力设计院"];
    const maxV = Math.max(...usageData.flat());
    const stepX = (w - 60) / (months.length - 1);

    usageData.forEach((data, di) => {
      const points = data.map((v, i) => ({
        x: 30 + i * stepX,
        y: h - 30 - ((v / maxV) * (h - 50)),
      }));
      ctx.beginPath();
      ctx.moveTo(points[0].x, points[0].y);
      points.forEach(p => ctx.lineTo(p.x, p.y));
      ctx.strokeStyle = colors[di];
      ctx.lineWidth = 2;
      ctx.stroke();
      points.forEach(p => {
        ctx.beginPath();
        ctx.arc(p.x, p.y, 3, 0, Math.PI * 2);
        ctx.fillStyle = colors[di];
        ctx.fill();
      });
    });

    months.forEach((m, i) => {
      ctx.fillStyle = "rgba(80,80,80,0.7)";
      ctx.font = "9px sans-serif";
      ctx.textAlign = "center";
      ctx.fillText(m, 30 + i * stepX, h - 12);
    });

    labels.forEach((l, i) => {
      ctx.fillStyle = colors[i];
      ctx.fillRect(30 + i * 120, 8, 12, 8);
      ctx.fillStyle = "rgba(80,80,80,0.8)";
      ctx.font = "9px sans-serif";
      ctx.textAlign = "left";
      ctx.fillText(l, 46 + i * 120, 16);
    });
  }, [activeTab]);

  return (
    <MainLayout>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-900">工商业用户档案</h1>
            <p className="text-sm text-gray-500 mt-0.5">共 {commercialUsers.length} 家工商业用户 · AI用能分析 · 合同管理</p>
          </div>
          <button className="flex items-center gap-2 px-4 py-2 bg-[#E8531A] text-white text-sm rounded-lg hover:bg-[#d44a15] cursor-pointer whitespace-nowrap">
            <i className="ri-building-2-line"></i>新增工商用户
          </button>
        </div>

        {/* AI Insights */}
        <div className="bg-gradient-to-r from-[#0f1729] to-[#1a2540] rounded-xl p-4 border border-white/10">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-6 h-6 flex items-center justify-center rounded-lg bg-[#E8531A]/20">
              <i className="ri-brain-line text-[#E8531A] text-sm"></i>
            </div>
            <span className="text-white text-sm font-semibold">AI 工商业用能智能分析</span>
            <span className="flex items-center gap-1 text-xs text-green-400 bg-green-400/10 px-2 py-0.5 rounded-full ml-auto">
              <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse inline-block"></span>
              实时分析
            </span>
          </div>
          <div className="grid grid-cols-4 gap-3">
            {aiInsights.map((item, i) => (
              <div key={i} className={`${item.bg} rounded-xl p-3`}>
                <div className="flex items-center gap-1.5 mb-1.5">
                  <i className={`${item.icon} ${item.color} text-sm`}></i>
                  <span className="text-xs font-semibold text-gray-800">{item.title}</span>
                </div>
                <p className="text-xs text-gray-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-gray-100 rounded-lg p-1 w-fit">
          {[
            { key: "list", label: "用户列表", icon: "ri-list-check" },
            { key: "portrait", label: "大用户画像", icon: "ri-user-star-line" },
            { key: "contract", label: "合同管理", icon: "ri-file-text-line" },
            { key: "analysis", label: "用能分析", icon: "ri-line-chart-line" },
          ].map(t => (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key as typeof activeTab)}
              className={`flex items-center gap-1.5 px-4 py-1.5 rounded-md text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${activeTab === t.key ? "bg-white text-[#E8531A] shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
            >
              <i className={t.icon}></i>{t.label}
            </button>
          ))}
        </div>

        {activeTab === "list" && (
          <>
            <div className="bg-white rounded-xl p-4 border border-gray-100 flex items-center gap-3 flex-wrap">
              <div className="relative flex-1 min-w-48">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                <input value={search} onChange={e => setSearch(e.target.value)} placeholder="搜索企业名称、联系人、编号..." className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-[#E8531A]/50" />
              </div>
              {types.map(t => (
                <button key={t} onClick={() => setFilterType(t)} className={`px-3 py-2 text-xs rounded-lg transition-colors cursor-pointer whitespace-nowrap ${filterType === t ? "bg-[#E8531A] text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>{t}</button>
              ))}
            </div>

            <div className="grid grid-cols-1 gap-4">
              {filtered.map(user => (
                <div key={user.id} className="bg-white rounded-xl border border-gray-100 p-5 hover:border-[#E8531A]/30 transition-colors">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-4 flex-1 min-w-0">
                      <div className="w-12 h-12 rounded-xl bg-orange-50 flex items-center justify-center flex-shrink-0">
                        <i className="ri-building-2-line text-[#E8531A] text-xl"></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="text-base font-bold text-gray-900">{user.name}</h3>
                          <span className="text-xs px-2 py-0.5 rounded-full bg-orange-50 text-[#E8531A]">{user.type}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${user.status === "正常" ? "bg-green-50 text-green-600" : "bg-red-50 text-red-600"}`}>{user.status}</span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">{user.address}</p>
                        <p className="text-xs text-gray-400 mt-0.5">联系人：{user.contact} · {user.phone} · 开户：{user.accountDate}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-6 flex-shrink-0">
                      <div className="text-center">
                        <p className="text-xs text-gray-400">本月用气</p>
                        <p className="text-base font-bold text-gray-900">{user.monthlyUsage.toLocaleString()} m³</p>
                      </div>
                      <div className="text-center">
                        <p className="text-xs text-gray-400">账户余额</p>
                        <p className="text-base font-bold text-[#E8531A]">¥{user.balance.toLocaleString()}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-xs text-gray-400">安全评级</p>
                        <p className="text-base font-bold text-green-600">{user.safetyGrade}</p>
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => navigate(`/profile/commercial/${user.id}`)} className="px-3 py-1.5 text-xs bg-[#E8531A] text-white rounded-lg hover:bg-[#d44a15] cursor-pointer whitespace-nowrap">查看详情</button>
                        <button className="px-3 py-1.5 text-xs bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 cursor-pointer whitespace-nowrap">购气</button>
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-gray-50 flex items-center gap-6">
                    <div className="flex items-center gap-1.5 text-xs text-gray-500">
                      <i className="ri-cpu-line text-gray-400"></i>
                      <span>设备 {user.devices.length} 台</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-xs text-gray-500">
                      <i className="ri-shield-check-line text-gray-400"></i>
                      <span>上次安检：{user.lastInspection}</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-xs text-gray-500">
                      <i className="ri-calendar-line text-gray-400"></i>
                      <span>下次安检：{user.nextInspection}</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-xs">
                      <i className={`ri-shield-line ${user.insurance.valid ? "text-green-500" : "text-red-400"}`}></i>
                      <span className={user.insurance.valid ? "text-green-600" : "text-red-500"}>
                        保险{user.insurance.valid ? `有效至${user.insurance.expiry}` : "已过期"}
                      </span>
                    </div>
                    <div className="ml-auto flex items-center gap-1.5 text-xs text-gray-400">
                      <i className="ri-bar-chart-line"></i>
                      <span>年用气：{user.yearlyUsage.toLocaleString()} m³</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {activeTab === "contract" && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-700">供气合同列表</span>
                <span className="text-xs bg-orange-50 text-[#E8531A] px-2 py-0.5 rounded-full">{contractData.length} 份</span>
              </div>
              <button className="flex items-center gap-1.5 text-xs bg-[#E8531A] text-white px-3 py-1.5 rounded-lg cursor-pointer hover:bg-[#d44a15] whitespace-nowrap">
                <i className="ri-add-line"></i>新建合同
              </button>
            </div>
            <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr>
                    {["合同编号", "企业名称", "合同类型", "开始日期", "到期日期", "合同金额", "状态", "操作"].map(h => (
                      <th key={h} className="text-left text-xs font-medium text-gray-500 px-4 py-3">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {contractData.map(item => (
                    <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 text-xs font-mono text-gray-500">{item.id}</td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-800">{item.company}</td>
                      <td className="px-4 py-3 text-xs text-gray-600">{item.type}</td>
                      <td className="px-4 py-3 text-xs text-gray-600">{item.startDate}</td>
                      <td className="px-4 py-3 text-xs text-gray-600">{item.endDate}</td>
                      <td className="px-4 py-3 text-sm font-bold text-[#E8531A]">{item.amount}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${item.status === "有效" ? "bg-green-50 text-green-600" : "bg-amber-50 text-amber-600"}`}>{item.status}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2">
                          <button className="text-xs text-[#E8531A] hover:underline cursor-pointer whitespace-nowrap">查看</button>
                          <button className="text-xs text-gray-400 hover:underline cursor-pointer whitespace-nowrap">续签</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="bg-amber-50 rounded-xl p-4 flex items-start gap-3">
              <i className="ri-robot-2-line text-amber-600 text-lg mt-0.5"></i>
              <div>
                <p className="text-sm font-semibold text-amber-700 mb-1">AI合同到期提醒</p>
                <p className="text-xs text-amber-600">北京华联超市供气合同将于 <strong>2026-06-30</strong> 到期，距今约 81 天。AI建议：提前45天启动续签谈判，参考市场气价趋势，预计续签金额约 ¥600,000/年（+3.4%）。</p>
              </div>
            </div>
          </div>
        )}

        {activeTab === "portrait" && <CommercialPortrait />}

        {activeTab === "analysis" && (
          <div className="space-y-4">
            <div className="bg-white rounded-xl border border-gray-100 p-5">
              <div className="flex items-center gap-2 mb-4">
                <i className="ri-line-chart-line text-[#E8531A]"></i>
                <span className="text-sm font-semibold text-gray-800">工商业用户用气量趋势（m³/月）</span>
              </div>
              <canvas ref={canvasRef} width={900} height={200} className="w-full" />
            </div>

            <div className="grid grid-cols-3 gap-4">
              {commercialUsers.map(user => (
                <div key={user.id} className="bg-white rounded-xl border border-gray-100 p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-xl bg-orange-50 flex items-center justify-center">
                      <i className="ri-building-2-line text-[#E8531A] text-lg"></i>
                    </div>
                    <div>
                      <p className="text-sm font-bold text-gray-800 truncate">{user.name}</p>
                      <p className="text-xs text-gray-400">{user.type}</p>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-400">本月用气</span>
                      <span className="font-bold text-gray-800">{user.monthlyUsage.toLocaleString()} m³</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-400">年累计</span>
                      <span className="font-medium text-gray-700">{user.yearlyUsage.toLocaleString()} m³</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-400">账户余额</span>
                      <span className="font-bold text-[#E8531A]">¥{user.balance.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-400">安全评级</span>
                      <span className="font-bold text-green-600">{user.safetyGrade}</span>
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t border-gray-50">
                    <div className="flex items-center gap-1.5 text-xs text-gray-500">
                      <i className="ri-robot-2-line text-[#E8531A]"></i>
                      <span className="text-[#E8531A] font-medium">AI分析：</span>
                      <span>用气量{user.monthlyUsage > 5000 ? "较大，建议大额充值" : "正常，按需充值即可"}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
}
